import { shopifySetupProgress } from '@discovr/features/shopify';
import { shopifyCopy } from '@discovr/features/copy';
import type { ShopifyConversionsSummary } from '@discovr/types';
import { CheckCircle, Circle } from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const setupCopy = shopifyCopy.report.setup;

const STEPS = ['connected', 'codes', 'visit', 'order'] as const;

export type ShopifyHelpAnchor = 'shopify-test-order' | 'shopify-metrics' | 'shopify-codes';

export function ShopifyHelpLink({ anchor, children }: { anchor: ShopifyHelpAnchor; children: string }) {
  return (
    <a
      href={`/help#${anchor}`}
      target="_blank"
      rel="noreferrer"
      className="text-foreground underline underline-offset-2"
    >
      {children} <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
}

/** Connected, codes, first visit, first order: done or pending from the summary. */
export function ShopifySetupStrip({
  summary,
  helpLink,
  className,
}: {
  summary: ShopifyConversionsSummary;
  helpLink?: boolean;
  className?: string;
}) {
  const progress = shopifySetupProgress(summary);
  if (!progress.connected || progress.order) return null;
  const testOrder = (summary.totals.test_orders ?? 0) > 0;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <ol
        aria-label={setupCopy.label}
        className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-caption-regular"
      >
        {STEPS.map((key) => {
          const done = progress[key];
          const Icon = done ? CheckCircle : Circle;
          return (
            <li key={key} data-step={key} data-done={done} className="flex items-center gap-1">
              <Icon
                weight={done ? 'fill' : 'regular'}
                className={cn('size-3.5', done ? 'text-emerald-700' : 'text-muted-foreground')}
                aria-hidden="true"
              />
              <span className={cn(done && 'text-foreground')}>{setupCopy.steps[key]}</span>
              <span className="sr-only">
                : {done ? setupCopy.done : setupCopy.pending}
              </span>
            </li>
          );
        })}
      </ol>
      {testOrder ? (
        <p role="status" className="text-caption-medium text-emerald-700">
          {setupCopy.testOrder}
        </p>
      ) : helpLink ? (
        <p className="text-muted-foreground text-caption-regular">
          <ShopifyHelpLink anchor="shopify-test-order">
            {shopifyCopy.report.help.testOrder}
          </ShopifyHelpLink>
        </p>
      ) : null}
    </div>
  );
}
